/**
 * Página de Información de Contratos, Convenios y Encomiendas - Portal de Transparencia
 * Información sobre contratos públicos y convenios de INTERCASA, S.A.
 * Estilo Awwwards con animaciones GSAP y diseño inmersivo
 */

import React, { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import LocalizedLink from '../../components/common/LocalizedLink';
import { useTranslation, Trans } from 'react-i18next';
import ImmersiveLayout from '../../components/layout/ImmersiveLayout';
import { AnimatedSection } from '../../components/animations';

/**
 * Página de Información de Contratos, Convenios y Encomiendas
 * @returns {JSX.Element} Página de información de contratos
 */
const ContractsInfoPage = () => {
  const { t } = useTranslation('transparency');
  const location = useLocation();

  /**
   * Efecto para hacer scroll al inicio de la página al cargar
   */
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
    
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      if (window.__lenis) {
        window.__lenis.scrollTo(0, { immediate: true });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.key]);

  return (
    <ImmersiveLayout>
      {/* Hero Section - Estilo Portal Transparencia */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen en patrón repetido (tile) */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(/images/banners/banner-negro.webp)',
            backgroundSize: '350px auto',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />
        
        <div className="relative z-10 container mx-auto px-4 md:px-8">
          <div className="max-w-4xl">
            {/* Link de regreso */}
            <LocalizedLink 
              to="/transparencia"
              className="group inline-flex items-center text-white/60 hover:text-white mb-8 transition-colors duration-300"
            >
              <svg className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium tracking-wide">{t('backToPortal')}</span>
            </LocalizedLink>
            
            <h1 className="font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl leading-tight mb-6 drop-shadow-lg">
              <Trans i18nKey="contracts.hero.title" ns="transparency" components={{ hl: <span className="text-intercasa-yellow" /> }} />
            </h1>
            
            <p className="text-lg md:text-xl text-white/60 max-w-2xl font-light">
              {t('contracts.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contenido */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-5xl mx-auto">
          
            {/* Convenios y Encomiendas de Gestión */}
            <AnimatedSection animation="fadeUp" className="mb-16">
              <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
                {t('year2024')}
              </span>
              <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl mb-8">
                {t('contracts.title')}
              </h2>
              
              <div className="space-y-6">
                {/* Estado principal */}
                <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                  <p className="text-intercasa-dark/70 leading-relaxed">
                    <Trans i18nKey="contracts.none" ns="transparency" components={{ bold: <strong className="text-intercasa-dark" /> }} />
                </p>
                </div>

                {/* Modificaciones */}
                <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                  <h3 className="font-baskerville font-bold text-intercasa-dark text-xl mb-4">
                    {t('contracts.modifications.title')}
                  </h3>
                  <p className="text-intercasa-dark/70 leading-relaxed">
                    <Trans i18nKey="contracts.modifications.text" ns="transparency" components={{ bold: <strong className="text-intercasa-dark" /> }} />
                  </p>
                </div>
              </div>

              {/* Botones de descarga */}
              <div className="flex flex-wrap gap-4 justify-center mt-8">
                <a 
                  href="/PortalTansparenciaIntercasa/ConvenioyCOntrato/ConveniosyEncomiendasIntercasa.pdf" 
                  download
                  className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 font-lato font-medium"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {t('download.pdf')}
                </a>
                <a
                  href="/PortalTansparenciaIntercasa/ConvenioyCOntrato/ConveniosyEncomiendasIntercasa.odt"
                  download
                  className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 font-lato font-medium"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {t('download.odt')}
                </a>
              </div>
            </AnimatedSection>

          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default ContractsInfoPage;